import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ShieldCheck, ArrowUpRight, Smartphone, Battery } from "lucide-react";
import { api } from "@/lib/api";
import { useNav } from "@/context/NavContext";

export default function ProtectionBanner() {
  const navigate = useNavigate();
  const nav = useNav();
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    api.get("/protection/settings").then((r) => setSettings(r.data)).catch(() => {});
  }, []);

  if (nav && !nav.loading && !nav.isVisible("schutzpaket")) return null;

  return (
    <section data-testid="protection-banner" className="relative z-10 bg-[#0a0a0a] py-20 lg:py-28 border-t border-white/10 overflow-hidden">
      <div className="pointer-events-none absolute -right-32 top-1/2 -translate-y-1/2 w-[480px] h-[480px] rounded-full bg-emerald-500/[0.07] blur-3xl" />
      <div className="max-w-7xl mx-auto px-6 lg:px-10 relative">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          className="rounded-[2rem] border border-emerald-500/20 bg-gradient-to-br from-[#0d1410] to-[#0a0a0a] p-8 lg:p-14 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center"
        >
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 rounded-full border border-emerald-500/30 bg-emerald-500/10 px-3.5 py-1.5 mb-6">
              <ShieldCheck size={14} className="text-emerald-400" />
              <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-emerald-300">Neu: G-Repair Schutzpaket</span>
            </div>
            <h2 className="font-display text-white text-3xl sm:text-4xl lg:text-5xl leading-[1.02] mb-5">
              Einmal zahlen.<br className="hidden sm:block" /> Jedes Jahr abgesichert.
            </h2>
            <p className="text-[#aaa] text-base max-w-lg leading-relaxed mb-8">
              Schließe für dein iPhone ein monatliches Schutzpaket ab und bekomme jedes Jahr eine Reparatur –
              Display oder Akku – ohne Zusatzkosten außer dem Versand.
            </p>
            <div className="flex flex-wrap items-center gap-4">
              <button onClick={() => navigate("/schutzpaket")} data-testid="protection-banner-cta"
                className="group inline-flex items-center gap-2.5 rounded-full bg-white text-[#050505] pl-6 pr-3 py-3 text-sm font-medium hover:bg-[#d1d5db] transition-colors duration-300">
                Schutzpaket entdecken
                <span className="w-8 h-8 rounded-full bg-[#050505] text-white flex items-center justify-center group-hover:rotate-45 transition-transform duration-300">
                  <ArrowUpRight size={14} />
                </span>
              </button>
              {settings && (
                <span data-testid="protection-banner-price" className="font-mono text-sm text-[#ccc]">
                  ab {Math.min(...Object.values(settings.tiers)).toFixed(2)} € / Monat
                </span>
              )}
            </div>
          </div>

          <div className="lg:col-span-5 grid grid-cols-2 gap-4">
            <div className="rounded-2xl border border-white/10 bg-black/30 p-6">
              <Smartphone size={22} className="text-emerald-400 mb-4" />
              <p className="text-white text-sm font-medium">Display</p>
              <p className="text-[#888] text-xs mt-1">Original-OLED inklusive</p>
            </div>
            <div className="rounded-2xl border border-white/10 bg-black/30 p-6">
              <Battery size={22} className="text-emerald-400 mb-4" />
              <p className="text-white text-sm font-medium">Akku</p>
              <p className="text-[#888] text-xs mt-1">Nach Wahl pro Jahr</p>
            </div>
            <div className="col-span-2 rounded-2xl border border-white/10 bg-black/30 p-5 flex items-center justify-between text-xs">
              <span className="text-[#888]">Mindestlaufzeit</span>
              <span className="text-white font-mono">{settings ? Math.round(settings.min_term_months / 12) : 2} Jahre, danach monatlich kündbar</span>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
